import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { MetodoPago, Producto, Venta } from '@/types/database'
import { getPrecio } from '@/features/productos/hooks'

export interface VentaFiltros {
  tipo?: 'minorista' | 'mayorista'
  fechaDesde?: string
  fechaHasta?: string
}

export function useVentas(filtros: VentaFiltros = {}) {
  return useQuery({
    queryKey: ['ventas', filtros],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '500' })
      if (filtros.tipo)       params.set('tipo', filtros.tipo)
      if (filtros.fechaDesde) params.set('desde', filtros.fechaDesde)
      if (filtros.fechaHasta) params.set('hasta', filtros.fechaHasta)
      return api.get<Venta[]>(`/api/ventas?${params}`)
    },
    staleTime: 30_000,
  })
}

export interface CartItem {
  producto: Producto
  presentacionId: string
  precioUnitario: number
  cantidad: number
  descuentoPct: number
  esLibre?: boolean
  descripcionLibre?: string
}

export function getCartTotal(items: CartItem[]): number {
  return items.reduce(
    (s, i) => s + i.precioUnitario * i.cantidad * (1 - i.descuentoPct / 100),
    0
  )
}

export function getPrecioParaTipo(
  producto: Producto,
  tipo: 'minorista' | 'mayorista',
): number {
  const precio = getPrecio(producto, tipo)
  if (precio > 0) return precio
  return getPrecio(producto, 'minorista')
}

interface ConfirmarVentaInput {
  tipo: 'minorista' | 'mayorista'
  items: CartItem[]
  metodoPago: MetodoPago
  montoPagado: number
  clienteId: string | null
  usuarioId: string
  depositoId: string
  notas?: string
  cajaId?: string | null
  clienteSaldoActual?: number
  clienteLimiteCredito?: number
}

export function useConfirmarVenta() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      tipo, items, metodoPago, montoPagado,
      clienteId, depositoId, notas,
      cajaId, clienteSaldoActual, clienteLimiteCredito,
    }: ConfirmarVentaInput) => {
      const subtotalBruto = items.reduce((s, i) => s + i.precioUnitario * i.cantidad, 0)
      const descuentoTotal = items.reduce(
        (s, i) => s + i.precioUnitario * i.cantidad * (i.descuentoPct / 100),
        0
      )
      const total = subtotalBruto - descuentoTotal

      // Validar límite de crédito antes de enviar
      if (metodoPago === 'cuenta_corriente' && clienteId && clienteSaldoActual !== undefined) {
        if (clienteLimiteCredito && clienteLimiteCredito > 0) {
          const disponible = clienteLimiteCredito - clienteSaldoActual
          if (total > disponible) {
            throw new Error(`Límite de crédito superado. Disponible: $${disponible.toFixed(2)}`)
          }
        }
      }

      const resultado = await api.post<{ id: string; numero: number }>('/api/ventas', {
        tipo,
        cliente_id:      clienteId,
        subtotal:        subtotalBruto,
        descuento_total: descuentoTotal,
        total,
        notas:           notas?.trim() || null,
        caja_id:         cajaId ?? null,
        items: items.map(i => ({
          producto_id:       i.esLibre ? null : i.producto.id,
          presentacion_id:   i.esLibre ? null : i.presentacionId,
          descripcion_libre: i.esLibre ? (i.descripcionLibre ?? i.producto.nombre) : null,
          cantidad:          i.cantidad,
          precio_unitario:   i.precioUnitario,
          descuento:         i.descuentoPct,
          subtotal:          i.precioUnitario * i.cantidad * (1 - i.descuentoPct / 100),
          deposito_id:       depositoId,
        })),
        pagos: [{
          metodo:  metodoPago,
          monto:   metodoPago === 'efectivo' ? Math.min(montoPagado, total) : total,
        }],
      })

      return { ventaId: resultado.id, numero: resultado.numero }
    },

    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['productos'] })
      qc.invalidateQueries({ queryKey: ['stock-page'] })
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] })
      qc.invalidateQueries({ queryKey: ['dashboard-alertas'] })
      qc.invalidateQueries({ queryKey: ['dashboard-movimientos'] })
      qc.invalidateQueries({ queryKey: ['movimientos-recientes'] })
      qc.invalidateQueries({ queryKey: ['ventas'] })
      qc.invalidateQueries({ queryKey: ['caja-movimientos'] })
      qc.invalidateQueries({ queryKey: ['caja-actual'] })
      qc.invalidateQueries({ queryKey: ['clientes'] })
    },
  })
}

interface AnularVentaInput {
  ventaId: string
  numero: number
  items: Array<{ producto_id: string; cantidad: number }>
  depositoId: string
  usuarioId: string
  tieneCC: boolean
  clienteId?: string | null
  totalCC?: number
  cajaActualId?: string | null
  metodoPago?: string
  monto?: number
}

export function useAnularVenta() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ ventaId, cajaActualId }: AnularVentaInput) =>
      api.put(`/api/ventas/${ventaId}/anular`, { caja_id: cajaActualId ?? null }),

    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['ventas'] })
      qc.invalidateQueries({ queryKey: ['productos'] })
      qc.invalidateQueries({ queryKey: ['stock-page'] })
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] })
      qc.invalidateQueries({ queryKey: ['dashboard-alertas'] })
      qc.invalidateQueries({ queryKey: ['dashboard-movimientos'] })
      qc.invalidateQueries({ queryKey: ['movimientos-recientes'] })
      qc.invalidateQueries({ queryKey: ['caja-movimientos'] })
      qc.invalidateQueries({ queryKey: ['caja-actual'] })
      if (vars.tieneCC && vars.clienteId) {
        qc.invalidateQueries({ queryKey: ['clientes'] })
        qc.invalidateQueries({ queryKey: ['cc-movimientos', vars.clienteId] })
      }
    },
  })
}

interface DevolucionItemInput {
  venta_item_id: string
  producto_id: string
  cantidad: number
  monto_unitario: number
}

interface CrearDevolucionInput {
  ventaId: string
  items: DevolucionItemInput[]
  motivo: string
  usuarioId: string
  depositoId: string
  tieneCC: boolean
  clienteId?: string | null
  cajaActualId?: string | null
  metodoPago?: string
}

export function useEnviarRecibo() {
  return useMutation({
    mutationFn: (ventaId: string) =>
      api.post<{ ok: boolean }>(`/api/ventas/${ventaId}/enviar-recibo`, {}),
  })
}

export function useCrearDevolucion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ventaId, items, motivo, depositoId, clienteId, cajaActualId, metodoPago }: CrearDevolucionInput) => {
      const total = items.reduce((s, i) => s + i.monto_unitario * i.cantidad, 0)
      return api.post<{ id: string; numero: number }>('/api/devoluciones', {
        venta_id:   ventaId,
        motivo:     motivo.trim() || null,
        total,
        caja_id:    cajaActualId ?? null,
        metodo_pago: metodoPago ?? null,
        items: items.map(i => ({
          venta_item_id:  i.venta_item_id,
          producto_id:    i.producto_id,
          cantidad:       i.cantidad,
          monto_unitario: i.monto_unitario,
          subtotal:       i.monto_unitario * i.cantidad,
          deposito_id:    depositoId,
          cliente_id:     clienteId ?? null,
        })),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ventas'] })
      qc.invalidateQueries({ queryKey: ['productos'] })
      qc.invalidateQueries({ queryKey: ['stock-page'] })
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] })
      qc.invalidateQueries({ queryKey: ['dashboard-alertas'] })
      qc.invalidateQueries({ queryKey: ['dashboard-movimientos'] })
      qc.invalidateQueries({ queryKey: ['movimientos-recientes'] })
      qc.invalidateQueries({ queryKey: ['caja-movimientos'] })
      qc.invalidateQueries({ queryKey: ['caja-actual'] })
      qc.invalidateQueries({ queryKey: ['clientes'] })
    },
  })
}
